import { supabase } from '../lib/supabase';

const BUCKET = 'brand-assets';
const BRAND_CACHE_KEY = 'brand_settings_cache';

const ALL_BRAND_KEYS = [
  'brand_header_logo_url',
  'brand_footer_logo_url',
  'brand_favicon_url',
  'brand_banner_url',
  'brand_mobile_banner_url',
  'brand_header_logo_scale',
  'brand_footer_logo_scale',
  'theme_color_primary',
  'theme_color_secondary',
  'theme_color_text',
  'theme_color_background',
  'theme_color_button',
  'theme_color_link',
  'theme_color_hover',
  'theme_color_accent',
  'theme_color_menu_icon',
  'theme_color_footer',
  'theme_color_hero_cta_gradient_start',
  'theme_color_hero_cta_gradient_end',
];

/**  الألوان الافتراضية */
export const DEFAULT_COLORS = {
  primary: '#2563EB',
  secondary: '#F07070',
  text: '#0f172a',
  background: '#FFFFFF',
  button: '#2563EB', // لون الأزرار منفصل عن اللون الأساسي
  link: '#2563EB',
  hover: '#1D4ED8',
  accent: '#F5C24A',
  menuIcon: '#9333ea',
  footer: '#f8fafc', // لون الفوتر
  /** تدرج زر «ابدأ الحجز الآن» في هيرو الصفحة الرئيسية */
  heroCtaGradientStart: '#2563EB',
  heroCtaGradientEnd: '#1D4ED8',
};

/**
 * خدمة إعدادات الهوية البصرية
 * تدير الشعارات + البانر + الحجم + ألوان الموقع
 */
/** شكل كائن الإعدادات كما يُرجَع من getBrandSettings */
// تحويل قيمة من DB إلى null إذا كانت فارغة أو النص "null" أو سلسلة فارغة
function dbVal(v) {
  if (!v || v === 'null' || v === '') return null;
  return v;
}

function buildBrandFromMap(m) {
  return {
    headerLogoUrl: dbVal(m.brand_header_logo_url),
    footerLogoUrl: dbVal(m.brand_footer_logo_url),
    faviconUrl: dbVal(m.brand_favicon_url),
    bannerUrl: dbVal(m.brand_banner_url),
    mobileBannerUrl: dbVal(m.brand_mobile_banner_url),
    headerLogoScale: parseInt(m.brand_header_logo_scale, 10) || 100,
    footerLogoScale: parseInt(m.brand_footer_logo_scale, 10) || 100,
    colors: {
      primary: m.theme_color_primary || DEFAULT_COLORS.primary,
      secondary: m.theme_color_secondary || DEFAULT_COLORS.secondary,
      text: m.theme_color_text || DEFAULT_COLORS.text,
      background: m.theme_color_background || DEFAULT_COLORS.background,
      button: m.theme_color_button || DEFAULT_COLORS.button,
      link: m.theme_color_link || DEFAULT_COLORS.link,
      hover: m.theme_color_hover || DEFAULT_COLORS.hover,
      accent: m.theme_color_accent || DEFAULT_COLORS.accent,
      menuIcon: m.theme_color_menu_icon || DEFAULT_COLORS.menuIcon,
      footer: m.theme_color_footer || DEFAULT_COLORS.footer,
      heroCtaGradientStart: m.theme_color_hero_cta_gradient_start || DEFAULT_COLORS.heroCtaGradientStart,
      heroCtaGradientEnd: m.theme_color_hero_cta_gradient_end || DEFAULT_COLORS.heroCtaGradientEnd,
    },
  };
}

export const brandSettingsService = {

  /**
   * قراءة الإعدادات من التخزين المحلي (للاستخدام فور التحميل وتجنّب وميض البانر/الألوان القديمة)
   */
  getCachedBrand() {
    if (typeof localStorage === 'undefined') return null;
    try {
      const raw = localStorage.getItem(BRAND_CACHE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || typeof data !== 'object') return null;
      // تنظيف قيم "null" المخزنة قديماً
      Object.keys(data).forEach(k => { if (data[k] === 'null') data[k] = null; });
      return buildBrandFromMap(data);
    } catch {
      return null;
    }
  },

  /**
   * حفظ نسخة من الإعدادات في التخزين المحلي (يُستدعى بعد جلب الإعدادات من الخادم)
   */
  setCachedBrand(settings) {
    if (typeof localStorage === 'undefined' || !settings) return;
    try {
      const m = {
        brand_header_logo_url: settings.headerLogoUrl,
        brand_footer_logo_url: settings.footerLogoUrl,
        brand_favicon_url: settings.faviconUrl,
        brand_banner_url: settings.bannerUrl,
        brand_mobile_banner_url: settings.mobileBannerUrl,
        brand_header_logo_scale: settings.headerLogoScale,
        brand_footer_logo_scale: settings.footerLogoScale,
        theme_color_primary: settings.colors?.primary,
        theme_color_secondary: settings.colors?.secondary,
        theme_color_text: settings.colors?.text,
        theme_color_background: settings.colors?.background,
        theme_color_button: settings.colors?.button,
        theme_color_link: settings.colors?.link,
        theme_color_hover: settings.colors?.hover,
        theme_color_accent: settings.colors?.accent,
        theme_color_menu_icon: settings.colors?.menuIcon,
        theme_color_footer: settings.colors?.footer,
        theme_color_hero_cta_gradient_start: settings.colors?.heroCtaGradientStart,
        theme_color_hero_cta_gradient_end: settings.colors?.heroCtaGradientEnd,
      };
      localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(m));
    } catch (_) {}
  },

  /**
   * جلب جميع إعدادات الهوية البصرية من الخادم
   */
  async getBrandSettings() {
    const { data, error } = await supabase
      .from('app_settings')
      .select('key, value')
      .in('key', ALL_BRAND_KEYS);

    if (error) {
      console.error('❌ Error fetching brand settings:', error);
      throw error;
    }

    const m = {};
    (data || []).forEach(row => { m[row.key] = row.value; });

    const settings = buildBrandFromMap(m);
    brandSettingsService.setCachedBrand(settings);
    return settings;
  },

  /**
   * رفع ملف إلى سلة brand-assets
   */
  async uploadBrandAsset(file, type) {
    const allowedTypes = {
      'header-logo': ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'],
      'footer-logo': ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'],
      'favicon': ['image/png', 'image/x-icon', 'image/vnd.microsoft.icon', 'image/ico', 'image/jpeg', 'image/svg+xml'],
      'banner': ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'],
      'mobile-banner': ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'],
    };

    const allowed = allowedTypes[type] || [];
    if (!allowed.includes(file.type) && !file.name.endsWith('.ico')) {
      throw new Error(`نوع الملف غير مدعوم. الأنواع المسموحة: ${allowed.join(', ')}`);
    }

    // البانر حتى 10MB، البقية 5MB
    const maxSize = (type === 'banner' || type === 'mobile-banner') ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error(`حجم الملف يجب أن يكون أقل من ${type === 'banner' ? '10' : '5'} ميجابايت`);
    }

    const ext = file.name.split('.').pop();
    const fileName = `${type}-${Date.now()}.${ext}`;
    const filePath = `${type}/${fileName}`;

    const { data, error } = await supabase.storage
      .from(BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
        contentType: file.type || 'application/octet-stream',
      });

    if (error) {
      console.error('❌ Error uploading brand asset:', error);
      throw new Error(`فشل رفع الملف: ${error.message}`);
    }

    const { data: urlData } = supabase.storage
      .from(BUCKET)
      .getPublicUrl(data.path);

    return urlData.publicUrl;
  },

  /**
   * حفظ جميع الإعدادات دفعة واحدة (batch upsert)
   * يدعم حفظ null كـ "" لضمان مسح الصور عند إزالتها
   */
  async saveAllBrandSettings(settings) {
    const mapping = [
      { key: 'brand_header_logo_url', val: settings.headerLogoUrl ?? '' },
      { key: 'brand_footer_logo_url', val: settings.footerLogoUrl ?? '' },
      { key: 'brand_favicon_url', val: settings.faviconUrl ?? '' },
      { key: 'brand_banner_url', val: settings.bannerUrl ?? '' },
      { key: 'brand_mobile_banner_url', val: settings.mobileBannerUrl ?? '' },
      { key: 'brand_header_logo_scale', val: String(settings.headerLogoScale ?? 100) },
      { key: 'brand_footer_logo_scale', val: String(settings.footerLogoScale ?? 100) },
    ];

    if (settings.colors) {
      mapping.push(
        { key: 'theme_color_primary', val: settings.colors.primary || DEFAULT_COLORS.primary },
        { key: 'theme_color_secondary', val: settings.colors.secondary || DEFAULT_COLORS.secondary },
        { key: 'theme_color_text', val: settings.colors.text || DEFAULT_COLORS.text },
        { key: 'theme_color_background', val: settings.colors.background || DEFAULT_COLORS.background },
        { key: 'theme_color_button', val: settings.colors.button || DEFAULT_COLORS.button },
        { key: 'theme_color_link', val: settings.colors.link || DEFAULT_COLORS.link },
        { key: 'theme_color_hover', val: settings.colors.hover || DEFAULT_COLORS.hover },
        { key: 'theme_color_accent', val: settings.colors.accent || DEFAULT_COLORS.accent },
        { key: 'theme_color_menu_icon', val: settings.colors.menuIcon || DEFAULT_COLORS.menuIcon },
        { key: 'theme_color_footer', val: settings.colors.footer || DEFAULT_COLORS.footer },
        { key: 'theme_color_hero_cta_gradient_start', val: settings.colors.heroCtaGradientStart || DEFAULT_COLORS.heroCtaGradientStart },
        { key: 'theme_color_hero_cta_gradient_end', val: settings.colors.heroCtaGradientEnd || DEFAULT_COLORS.heroCtaGradientEnd },
      );
    }

    // Batch upsert — كل الإعدادات في طلب واحد
    const rows = mapping.map(({ key, val }) => ({ key, value: String(val) }));

    const { error } = await supabase
      .from('app_settings')
      .upsert(rows, { onConflict: 'key', ignoreDuplicates: false });

    if (error) {
      console.error('❌ Error saving brand settings:', error);
      throw new Error(`فشل حفظ الإعدادات: ${error.message}`);
    }

    // تحديث الكاش المحلي
    brandSettingsService.setCachedBrand(settings);
  },

  /**
   * حذف ملف قديم من التخزين
   */
  async deleteOldAsset(url) {
    if (!url) return;
    try {
      const bucketUrl = `/storage/v1/object/public/${BUCKET}/`;
      const idx = url.indexOf(bucketUrl);
      if (idx === -1) return;
      const path = url.substring(idx + bucketUrl.length);
      await supabase.storage.from(BUCKET).remove([path]);
    } catch (err) {
      console.warn('⚠️ Could not delete old asset:', err);
    }
  },
};
